import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1, width: "100%", height: "100%", overflow: "auto",
    background: theme.palette.type === "dark" ? "#080E1C" : "#F8FAFC",
    fontFamily: "'Inter', system-ui, sans-serif",
    ...theme.scrollbarStyles,
  },
  inner: { maxWidth: 1100, margin: "0 auto", padding: "28px 20px", display: "flex", flexDirection: "column", gap: 20 },
  pageTitle: { fontWeight: 700, fontSize: "1.4rem", letterSpacing: "-0.3px", color: theme.palette.type === "dark" ? "#F1F5F9" : "#0F172A", marginBottom: 4 },
  pageSub: { fontSize: "0.88rem", color: "#64748B" },
  card: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E8EDF5"}`,
    borderRadius: 16, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  cardTitle: { fontWeight: 700, fontSize: "0.92rem", color: theme.palette.type === "dark" ? "#F1F5F9" : "#0F172A", marginBottom: 14 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 20 },
  statCard: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#F8FAFC",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#E8EDF5"}`,
    borderRadius: 12, padding: "14px 16px",
  },
  statNum: { fontWeight: 800, fontSize: "1.5rem", color: "#1D4ED8" },
  statLabel: { fontSize: "0.72rem", color: "#94A3B8", marginTop: 2 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "10px 14px", textAlign: "left", fontSize: "0.72rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px", color: "#94A3B8",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F1F5F9"}`,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 14px", fontSize: "0.82rem",
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#374151",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC"}`,
    verticalAlign: "middle",
  },
  balanceBar: { width: 80, height: 5, borderRadius: 3, background: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0", overflow: "hidden", display: "inline-block" },
  balanceFill: { height: "100%", borderRadius: 3, transition: "width 0.4s ease" },
  addBtn: {
    borderRadius: 8, fontWeight: 600, fontSize: "0.78rem",
    background: "#1D4ED8", color: "#fff", border: "none",
    padding: "6px 14px", cursor: "pointer", transition: "all 0.15s",
    "&:hover": { background: "#1E40AF" },
  },
  removeBtn: {
    borderRadius: 8, fontWeight: 600, fontSize: "0.78rem",
    background: "transparent", color: "#EF4444",
    border: "1px solid rgba(239,68,68,0.3)", padding: "6px 14px", cursor: "pointer",
  },
  input: {
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    borderRadius: 8, padding: "6px 10px", fontSize: "0.82rem", width: 80,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F8FAFC",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#1E293B",
    outline: "none", textAlign: "center",
  },
  searchInput: {
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    borderRadius: 10, padding: "9px 14px", fontSize: "0.84rem", width: "100%", maxWidth: 300,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F8FAFC",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#1E293B",
    outline: "none", boxSizing: "border-box",
  },
  alertBadge: {
    fontSize: "0.65rem", fontWeight: 700, padding: "3px 8px", borderRadius: 6,
  },
  pkgCard: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#F8FAFC",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E8EDF5"}`,
    borderRadius: 12, padding: "16px", display: "flex", alignItems: "center", gap: 14,
  },
  pkgInput: {
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    borderRadius: 8, padding: "6px 10px", fontSize: "0.8rem",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#fff",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#1E293B",
    outline: "none",
  },
}));

const ALERT_COLORS = { critical: "#EF4444", danger: "#F97316", warning: "#EAB308" };

const AICreditsAdmin = () => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState("");
  const [amounts, setAmounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("companies");
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const loadAnalytics = () => {
    setAnalyticsLoading(true);
    api.get("/ai-credits/admin/analytics")
      .then(r => setAnalytics(r.data))
      .catch(() => setAnalytics(null))
      .finally(() => setAnalyticsLoading(false));
  };

  const load = () => {
    api.get("/ai-credits/admin/companies").then(r => setCompanies(r.data)).catch(() => {});
    api.get("/ai-credits/packages").then(r => setPackages(r.data)).catch(() => {});
  };

  useEffect(() => { load(); loadAnalytics(); }, []);

  const handleAdd = async (companyId, amount) => {
    if (!amount || amount <= 0) return toast.warning("Informe uma quantidade válida");
    setLoading(true);
    try {
      await api.post("/ai-credits/admin/add", { companyId, amount: Number(amount) });
      toast.success(`✅ ${amount} créditos adicionados!`);
      setAmounts(prev => ({ ...prev, [companyId]: "" }));
      load();
    } catch { toast.error("Erro ao adicionar créditos"); }
    setLoading(false);
  };

  const handleRemove = async (companyId, amount) => {
    if (!amount || amount <= 0) return toast.warning("Informe uma quantidade válida");
    setLoading(true);
    try {
      await api.post("/ai-credits/admin/add", { companyId, amount: -Number(amount) });
      toast.success(`✅ ${amount} créditos removidos!`);
      setAmounts(prev => ({ ...prev, [companyId]: "" }));
      load();
    } catch { toast.error("Erro ao remover créditos"); }
    setLoading(false);
  };

  const handleResetAll = async () => {
    if (!window.confirm("Renovar 2000 créditos para TODAS as empresas?")) return;
    setLoading(true);
    try {
      await api.post("/ai-credits/admin/reset-all", { amount: 2000 });
      toast.success("✅ Créditos renovados para todas as empresas!");
      load();
    } catch { toast.error("Erro ao renovar créditos"); }
    setLoading(false);
  };

  const filtered = companies.filter(c => (c.companyName || c.name || "").toLowerCase().includes(search.toLowerCase()));
  const totalBalance = companies.reduce((a, c) => a + (c.balance || 0), 0);
  const totalConsumed = companies.reduce((a, c) => a + (c.totalConsumed || 0), 0);
  const lowCredit = companies.filter(c => c.alertLevel).length;

  const tabStyle = (tab) => ({
    padding: "8px 18px", borderRadius: 9, fontWeight: 600, fontSize: "0.82rem",
    cursor: "pointer", border: "none", transition: "all 0.15s",
    background: activeTab === tab ? "#1D4ED8" : "transparent",
    color: activeTab === tab ? "#fff" : "#64748B",
  });

  return (
    <div className={classes.root}>
      <div className={classes.inner}>

        <div>
          <div className={classes.pageTitle}>⚡ Admin — Créditos IA</div>
          <div className={classes.pageSub}>Gerencie os créditos de todas as empresas da plataforma.</div>
        </div>

        {/* ANALYTICS CARD */}
        <div className={classes.card} style={{ borderLeft: "3px solid #1D4ED8" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.92rem", marginBottom: 4 }}>📊 Consumo IA — Mês Atual</div>
              <div style={{ fontSize: "0.78rem", color: "#64748B" }}>Uso real da Korax IA em todas as empresas</div>
            </div>
            <button className={classes.addBtn} onClick={loadAnalytics} disabled={analyticsLoading}
              style={{ padding: "7px 14px", fontSize: "0.78rem" }}>
              ↻ Atualizar
            </button>
          </div>
          {analyticsLoading ? (
            <div style={{ color: "#94A3B8", fontSize: "0.82rem" }}>Carregando...</div>
          ) : analytics ? (
            <div>
              {/* TOTAIS */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
                <div className={classes.statCard}>
                  <div className={classes.statNum} style={{ color: "#1D4ED8" }}>{Number(analytics.totals?.total_mes || 0).toLocaleString()}</div>
                  <div className={classes.statLabel}>Créditos consumidos</div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statNum} style={{ color: "#8B5CF6" }}>{Number(analytics.totals?.total_usos || 0).toLocaleString()}</div>
                  <div className={classes.statLabel}>Interações IA</div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statNum} style={{ color: "#F97316" }}>R$ {analytics.estimatedCost?.brl || "0,00"}</div>
                  <div className={classes.statLabel}>Custo estimado (BRL)</div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statNum} style={{ color: "#22C55E" }}>${analytics.estimatedCost?.usd || "0.0000"}</div>
                  <div className={classes.statLabel}>Custo estimado (USD)</div>
                </div>
              </div>
              {/* TOP AGENTES */}
              {analytics.byAgent?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>Top Agentes</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {analytics.byAgent.slice(0, 5).map((a, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ flex: 1, fontSize: "0.82rem", fontWeight: 600 }}>{a.agentName || "—"}</div>
                        <div style={{ fontSize: "0.72rem", color: "#64748B" }}>{a.model}</div>
                        <div style={{ fontWeight: 700, color: "#1D4ED8", fontSize: "0.82rem", minWidth: 60, textAlign: "right" }}>{Number(a.creditos).toLocaleString()} créditos</div>
                        <div style={{ width: 80, height: 4, borderRadius: 2, background: "rgba(0,0,0,0.06)", overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 2, background: "#1D4ED8", width: `${Math.min(100, (a.creditos / (analytics.byAgent[0]?.creditos || 1)) * 100)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* CONSUMO POR DIA */}
              {analytics.byDay?.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>Últimos 30 dias</div>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 50 }}>
                    {analytics.byDay.slice(-30).map((d, i) => {
                      const max = Math.max(...analytics.byDay.map(x => Number(x.creditos)));
                      const h = max > 0 ? Math.max(4, Math.round((d.creditos / max) * 50)) : 4;
                      return (
                        <div key={i} title={`${d.dia}: ${d.creditos} créditos`}
                          style={{ flex: 1, height: h, borderRadius: 2, background: "#1D4ED8", opacity: 0.7, cursor: "pointer" }} />
                      );
                    })}
                  </div>
                </div>
              )}
              {analytics.totals?.total_mes == 0 && (
                <div style={{ color: "#94A3B8", fontSize: "0.82rem", textAlign: "center", padding: "12px 0" }}>
                  Nenhum consumo registrado este mês ainda.
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "#94A3B8", fontSize: "0.82rem" }}>Clique em Atualizar para carregar os dados.</div>
          )}
        </div>

        {/* STATS */}
        <div className={classes.statsGrid}>
          <div className={classes.statCard}>
            <div className={classes.statNum}>{companies.length}</div>
            <div className={classes.statLabel}>Empresas ativas</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statNum}>{totalBalance.toLocaleString()}</div>
            <div className={classes.statLabel}>Total créditos em saldo</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statNum}>{totalConsumed.toLocaleString()}</div>
            <div className={classes.statLabel}>Total consumido</div>
          </div>
          <div className={classes.statCard}>
            <div className={classes.statNum} style={{ color: lowCredit > 0 ? "#EF4444" : "#22C55E" }}>{lowCredit}</div>
            <div className={classes.statLabel}>Empresas com saldo baixo</div>
          </div>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", gap: 6, padding: "4px", background: "rgba(0,0,0,0.04)", borderRadius: 12, width: "fit-content" }}>
          <button style={tabStyle("companies")} onClick={() => setActiveTab("companies")}>🏢 Empresas</button>
          <button style={tabStyle("packages")} onClick={() => setActiveTab("packages")}>📦 Pacotes</button>
        </div>

        {activeTab === "companies" && (
          <div className={classes.card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
              <div className={classes.cardTitle} style={{ marginBottom: 0 }}>Todas as empresas</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input className={classes.searchInput} placeholder="Buscar empresa..." value={search} onChange={e => setSearch(e.target.value)} />
                <button className={classes.addBtn} onClick={handleResetAll} disabled={loading}
                  style={{ background: "#22C55E", padding: "9px 16px" }}>
                  ↻ Renovar todos (2000)
                </button>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>Empresa</th>
                    <th className={classes.th}>Saldo</th>
                    <th className={classes.th}>Progresso</th>
                    <th className={classes.th}>Consumido</th>
                    <th className={classes.th}>Status</th>
                    <th className={classes.th}>Qtd</th>
                    <th className={classes.th}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => {
                    const pct = c.monthlyIncluded > 0 ? Math.min(Math.round((c.balance / c.monthlyIncluded) * 100), 100) : 100;
                    const barColor = c.alertLevel ? ALERT_COLORS[c.alertLevel] : "#22C55E";
                    return (
                      <tr key={c.companyId}>
                        <td className={classes.td}>
                          <div style={{ fontWeight: 600 }}>{c.companyName || `Empresa #${c.companyId}`}</div>
                          <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>ID: {c.companyId}</div>
                        </td>
                        <td className={classes.td}>
                          <span style={{ fontWeight: 700, color: "#1D4ED8" }}>{(c.balance || 0).toLocaleString()}</span>
                          <span style={{ color: "#94A3B8", fontSize: "0.72rem" }}> / {(c.monthlyIncluded || 0).toLocaleString()}</span>
                        </td>
                        <td className={classes.td}>
                          <div className={classes.balanceBar}>
                            <div className={classes.balanceFill} style={{ width: `${pct}%`, background: barColor }} />
                          </div>
                          <span style={{ fontSize: "0.72rem", color: "#94A3B8", marginLeft: 6 }}>{pct}%</span>
                        </td>
                        <td className={classes.td}>{(c.totalConsumed || 0).toLocaleString()}</td>
                        <td className={classes.td}>
                          {c.alertLevel ? (
                            <span className={classes.alertBadge} style={{ background: `${ALERT_COLORS[c.alertLevel]}15`, color: ALERT_COLORS[c.alertLevel] }}>
                              {c.alertLevel === "critical" ? "🔴 Crítico" : c.alertLevel === "danger" ? "🟠 Baixo" : "🟡 Atenção"}
                            </span>
                          ) : (
                            <span className={classes.alertBadge} style={{ background: "rgba(34,197,94,0.1)", color: "#16A34A" }}>✅ Normal</span>
                          )}
                        </td>
                        <td className={classes.td}>
                          <input type="number" className={classes.input} placeholder="100"
                            value={amounts[c.companyId] || ""}
                            onChange={e => setAmounts(prev => ({ ...prev, [c.companyId]: e.target.value }))}
                            min="1" />
                        </td>
                        <td className={classes.td}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button className={classes.addBtn} disabled={loading}
                              onClick={() => handleAdd(c.companyId, amounts[c.companyId])}>
                              + Adicionar
                            </button>
                            <button className={classes.removeBtn} disabled={loading}
                              onClick={() => handleRemove(c.companyId, amounts[c.companyId])}>
                              − Remover
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "packages" && (
          <div className={classes.card}>
            <div className={classes.cardTitle}>Pacotes disponíveis para compra</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {packages.map(pkg => (
                <div key={pkg.id} className={classes.pkgCard}>
                  <span style={{ fontSize: "1.3rem" }}>⚡</span>
                  <div style={{ flex: 1, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>{pkg.name}</div>
                      <div style={{ fontSize: "0.72rem", color: "#94A3B8" }}>{pkg.credits.toLocaleString()} créditos</div>
                    </div>
                    <div style={{ fontWeight: 700, color: "#1D4ED8" }}>R$ {Number(pkg.price).toFixed(2).replace(".", ",")}</div>
                    {pkg.badge && <span style={{ fontSize: "0.68rem", fontWeight: 700, background: "rgba(29,78,216,0.1)", color: "#1D4ED8", padding: "3px 8px", borderRadius: 6 }}>{pkg.badge}</span>}
                    <span style={{ fontSize: "0.72rem", padding: "3px 8px", borderRadius: 6, fontWeight: 600, background: pkg.active ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", color: pkg.active ? "#16A34A" : "#DC2626" }}>
                      {pkg.active ? "✅ Ativo" : "❌ Inativo"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AICreditsAdmin;
